import { useTranslation } from 'react-i18next'
import { Checkbox, Grid, Theme, Stack } from '@dhl-ui/components'
import {
  getCheckboxStateProps,
  containsKey,
  getCheckboxState,
  ProductConfig,
  type ProductDataTableColumn,
} from '~/common/components/accountnumbers'
import { isProductConfigurable, Product, ProductGroup } from '~/common/model/capabilities'
import type { ReturnHub } from '~/modules/returnhubsccc/model'

const PRODUCTS_SORTED_LAST = ['EPA', 'EPD']

type StyleProps = {
  isAlternateRow: boolean
}

type Props = StyleProps & {
  productGroup: ProductGroup
  products: Product[]
  selectedProducts: Product[]
  onChangeSelectedProducts: (selectedProducts: Product[]) => void
  columns: ProductDataTableColumn[]
  disabled: boolean
  isCCCOrg: boolean
  returnHubs: ReturnHub[]
}

export const Row = ({
  isAlternateRow,
  productGroup,
  products,
  selectedProducts,
  onChangeSelectedProducts,
  columns,
  disabled,
  isCCCOrg,
  returnHubs,
}: Props) => {
  const rowItemSx = ({ spacing, palette: { grey } }: Theme) => ({
    padding: spacing(1.75, 2, 0, 3),
    color: grey[600],
    display: 'flex',
    alignItems: 'flex-start',
  })
  const rowLeftItemSx = (theme: Theme) => ({
    ...rowItemSx(theme),
    borderRight: `1px solid ${theme.palette.grey[300]}`,
    paddingLeft: 2.85,
  })
  const { t } = useTranslation()
  const selectAllState = getCheckboxState(selectedProducts, products)
  const columnWidth = 12 / (columns.length || 1)

  const handleSelectAll = (deselectAll: boolean) => {
    onChangeSelectedProducts(
      deselectAll ? [] : [...selectedProducts, ...products.filter((p) => !containsKey(selectedProducts, p.value))]
    )
  }

  const handleSelectProduct = (checked: boolean, product: Product) => {
    onChangeSelectedProducts(
      checked ? [...selectedProducts, product] : selectedProducts.filter((p) => p.value !== product.value)
    )
  }

  const handleChangeProduct = (product: Product) => {
    onChangeSelectedProducts(selectedProducts.map((p) => (p.value === product.value ? product : p)))
  }

  const getProductItems = (products: Product[], column: ProductDataTableColumn) => {
    return (
      columns.includes(column) && (
        <Grid container gap={1} size={{ xs: columnWidth }} direction="column" sx={rowItemSx}>
          {products
            .sort((p1, p2) => (PRODUCTS_SORTED_LAST.includes(p1.value) ? 1 : p1.value < p2.value ? -1 : 1))
            .map((product) => {
              const { label, value } = product
              const selectedProduct = selectedProducts.find((p) => p.value === value)
              const isSelected = !!selectedProduct
              return (
                <Stack key={value} gap={1}>
                  <Checkbox
                    onChange={(event, checked) => handleSelectProduct(checked, product)}
                    checked={isSelected}
                    disabled={disabled}
                    label={`${label} (${value})`}
                  />
                  {isCCCOrg && isProductConfigurable(product) && isSelected && (
                    <ProductConfig
                      selectedProduct={selectedProduct}
                      returnHubs={returnHubs}
                      onChangeProduct={handleChangeProduct}
                    />
                  )}
                </Stack>
              )
            })}
        </Grid>
      )
    )
  }

  return (
    <Grid container sx={({ palette: { grey } }) => ({ backgroundColor: isAlternateRow ? 'common.white' : grey[200] })}>
      {columns.includes('selectAll') && (
        <Grid size={{ xs: columnWidth }} sx={rowLeftItemSx}>
          <Checkbox
            onChange={(event, checked) => handleSelectAll(!checked)}
            {...getCheckboxStateProps(selectAllState)}
            label={t(`admin.accountNumbers.product_group.${productGroup}`)}
          />
        </Grid>
      )}
      {getProductItems(
        products.filter((p) => !p.isReturnProduct),
        'selectProduct'
      )}
      {getProductItems(
        products.filter((p) => p.isReturnProduct),
        'selectReturnProduct'
      )}
    </Grid>
  )
}
